import type { Answers } from "./_schema";
import { EN_FOCUS_COPY, type FocusCopyPack } from "./quiz-focus-copy";
import { deriveDayCurve } from "./quiz-focus-day";
import {
  FOCUS_CAUSES,
  type AttentionDrainLevel,
  type CognitivePotential,
  type FocusQol,
  type FocusCause,
  type FocusProfile,
} from "./_focus-schema";

/**
 * Derivation layer for the Ignite focus quiz's verdict.
 *
 * Everything in this file is a pure function of `answers`, so the whole
 * profile is reproducible server-side from the persisted submission (rule 11).
 * Nothing here imports from `web/`. Screen and option ids are the ones
 * authored in `quiz-focus.ts`; the two files are a contract.
 */

function str(answers: Answers, id: string): string | undefined {
  const v = answers[id];
  return typeof v === "string" ? v : undefined;
}

function list(answers: Answers, id: string): string[] {
  const v = answers[id];
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

/**
 * Tie-break order. Age is LAST on purpose: "it is your age" is the least
 * actionable and most deflating verdict available, so it has to genuinely
 * outscore the others to lead. A 46-year-old whose real problem is Slack must
 * not be told he is getting old.
 */
const CAUSE_ORDER: readonly FocusCause[] = [
  "interruption-tax",
  "cognitive-depletion",
  "multitasking-overload",
  "age-related-decline",
];

export type CauseScores = Record<FocusCause, number>;

export function scoreFocusCauses(answers: Answers): CauseScores {
  const s: CauseScores = {
    "interruption-tax": 0,
    "multitasking-overload": 0,
    "cognitive-depletion": 0,
    "age-related-decline": 0,
  };

  // Interruption tax
  const phone = str(answers, "phone-reach");
  if (phone === "constantly") s["interruption-tax"] += 3;
  else if (phone === "more-than-before") s["interruption-tax"] += 2;
  else if (phone === "sometimes") s["interruption-tax"] += 1;
  if (str(answers, "stakes") === "work") s["interruption-tax"] += 1;
  if (list(answers, "last-week").includes("didnt-hear")) s["interruption-tax"] += 1;

  // Multitasking overload
  const switching = str(answers, "task-switch");
  if (switching === "most-days") s["multitasking-overload"] += 3;
  else if (switching === "few-times-week") s["multitasking-overload"] += 2;
  else if (switching === "once-in-a-while") s["multitasking-overload"] += 1;
  if (str(answers, "stakes") === "goals") s["multitasking-overload"] += 1;
  if (str(answers, "if-nothing-changes") === "never-finish") s["multitasking-overload"] += 1;

  // Cognitive depletion
  const wall = str(answers, "afternoon-wall");
  if (wall === "almost-daily") s["cognitive-depletion"] += 3;
  else if (wall === "few-times-week") s["cognitive-depletion"] += 2;
  else if (wall === "after-bad-sleep") s["cognitive-depletion"] += 1;
  const coffee = str(answers, "coffee-effect");
  if (coffee === "wires-then-drops" || coffee === "need-more") s["cognitive-depletion"] += 2;
  const waking = str(answers, "waking");
  if (waking === "tired-anyway" || waking === "slow-start") s["cognitive-depletion"] += 2;
  if (str(answers, "stakes") === "energy") s["cognitive-depletion"] += 1;

  // Age-related decline, gated on age. Below the gate it scores nothing at all
  // and therefore cannot reach the page.
  const age = str(answers, "age");
  if (age === "45-54" || age === "55-plus") {
    s["age-related-decline"] += age === "55-plus" ? 3 : 2;
    if (str(answers, "reread") === "most-days") s["age-related-decline"] += 1;
    if (str(answers, "room") === "daily") s["age-related-decline"] += 1;
  }

  return s;
}

/** Primary then secondary. Always exactly two, always distinct. */
export function pickCauses(answers: Answers): [FocusCause, FocusCause] {
  const s = scoreFocusCauses(answers);
  const ranked = [...CAUSE_ORDER].sort((a, b) => {
    if (s[b] !== s[a]) return s[b] - s[a];
    return CAUSE_ORDER.indexOf(a) - CAUSE_ORDER.indexOf(b);
  });
  return [ranked[0]!, ranked[1]!];
}

export { FOCUS_CAUSES };

/**
 * The all-clear gate.
 *
 * A man who answers "none of these" to the last-week checklist, "rarely" to
 * task-switching, "no" to rereading, "no" to the afternoon wall, "rarely" to
 * walking into rooms, "rested" to waking AND "nothing serious yet" to what it
 * has cost him has told us on SEVEN separate screens that he is fine. Telling
 * him his attention is being drained is a lie he can detect, and he counted
 * the evidence himself.
 *
 * The cost screen is in the gate because the reverse is the same lie: a man
 * who has just said "there's nothing left of me by six" must never be told he
 * has performance headroom two screens later. The result-page spec listed six
 * screens because the cost screen was not in the flow when it was written.
 *
 * Same exemption the cortisol advertorial quiz already runs against the
 * rule-12a floor, and it must stay exactly this narrow: seven clean answers,
 * checked positively rather than by a score threshold, so a future scoring
 * tweak cannot widen it by accident. Tightening it further is cheap; loosening
 * it is the thing to argue about.
 */
export function isAllClear(answers: Answers): boolean {
  const week = list(answers, "last-week");
  return (
    week.length === 1 &&
    week[0] === "none" &&
    str(answers, "task-switch") === "rarely" &&
    str(answers, "reread") === "no" &&
    str(answers, "afternoon-wall") === "no" &&
    str(answers, "room") === "rarely" &&
    str(answers, "waking") === "rested" &&
    str(answers, "cost") === "nothing-yet"
  );
}

/** Heavy signals: things he reports happening, not things he infers. */
function heavySignals(answers: Answers): number {
  let n = list(answers, "last-week").filter((x) => x !== "none").length;
  if (str(answers, "afternoon-wall") === "almost-daily") n += 1;
  if (str(answers, "reread") === "most-days") n += 1;
  if (str(answers, "room") === "daily") n += 1;
  if (str(answers, "waking") === "waking-at-2") n += 1;
  return n;
}

function mediumSignals(answers: Answers): number {
  let n = 0;
  if (str(answers, "task-switch") === "most-days") n += 1;
  if (str(answers, "phone-reach") === "constantly") n += 1;
  if (str(answers, "coffee-effect") === "wires-then-drops") n += 1;
  const dur = str(answers, "how-long");
  if (dur === "years" || dur === "over-a-year") n += 1;
  return n;
}

/**
 * Floors at `detected` for anyone who is not all-clear. DETECTED confirms the
 * problem is present rather than grading it politely -- the same reasoning
 * that removed NORMAL and then MILD from the Boost verdict (rule 13).
 */
export function deriveDrainLevel(answers: Answers): AttentionDrainLevel {
  if (isAllClear(answers)) return "minimal";
  const score = heavySignals(answers) * 2 + mediumSignals(answers);
  if (score >= 10) return "severe";
  if (score >= 6) return "high";
  if (score >= 3) return "moderate";
  return "detected";
}

/**
 * Reach of the consequence. Floors at `emerging` (never "low"), same
 * no-absolution reason as the drain floor.
 */
export function deriveQol(answers: Answers): FocusQol {
  const cost = str(answers, "cost");
  const reported = list(answers, "last-week").filter((x) => x !== "none").length;
  let score = reported;
  if (cost === "work-slipping") score += 1;
  else if (cost === "people-noticed") score += 2;
  else if (cost === "outpaced") score += 2;
  else if (cost === "evenings-gone") score += 3;

  if (score >= 5) return "pervasive";
  if (score >= 3) return "disruptive";
  if (score >= 1) return "noticeable";
  return "emerging";
}

/**
 * The one optimistic cell. Every value is flattering, so this can never read
 * as a fourth piece of bad news; it moves so the grid reads as computed rather
 * than printed.
 *
 * Mapping is the result-page spec's, verbatim: under a year plus an external
 * primary cause reads EXCEPTIONAL; depletion, or over a year, reads VERY HIGH;
 * an age-related primary, or "years now", reads HIGH. (The plan narrowed
 * EXCEPTIONAL to under six months and sent "over a year" to HIGH, which left
 * the cell on its floor for most men; the spec's rationale is that it MOVES.)
 */
export function derivePotential(answers: Answers, primary: FocusCause): CognitivePotential {
  const dur = str(answers, "how-long");
  if (primary === "age-related-decline") return "high";
  if (dur === "years") return "high";
  const underAYear = dur === "lt-6-months" || dur === "6-12-months";
  const external = primary === "interruption-tax" || primary === "multitasking-overload";
  if (underAYear && external) return "exceptional";
  return "very-high";
}

/**
 * The whole verdict, as a pure function of answers. Reproducible server-side
 * from the persisted `answers` JSON (rule 11).
 *
 * `copy` is the locale seam, same shape as the Boost deriver's: swapping it
 * changes prose only and no enum output at all, so a future translation cannot
 * move any locked distribution.
 */
export function deriveFocusProfile(
  answers: Answers,
  opts: { copy?: FocusCopyPack } = {},
): FocusProfile {
  const copy = opts.copy ?? EN_FOCUS_COPY;
  const drainLevel = deriveDrainLevel(answers);
  const causes = pickCauses(answers);
  const primary = causes[0];
  const allClear = drainLevel === "minimal";
  const tone = drainLevel === "high" || drainLevel === "severe" ? "harder" : "softer";

  return {
    drainLevel,
    causes,
    qualityOfLifeImpact: allClear ? "emerging" : deriveQol(answers),
    cognitivePotential: allClear ? "exceptional" : derivePotential(answers, primary),
    assessment: allClear
      ? {
          label: copy.headroomLabel,
          headline: copy.headroomHeadline,
          explanation: copy.headroomExplanation,
        }
      : {
          label: copy.label[primary],
          headline: copy.headline[primary],
          explanation: copy.explanation[primary][tone],
        },
    dayCurve: deriveDayCurve(answers),
  };
}
